import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Container,
  Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { AddEmployee } from "../../../Services/Services"; // ✅ Correct import

function AddEmployeeComponent() { // Name change kiya conflict se bachne ke liye
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);

  const initialFormState = {
    full_name: "",
    email: "",
    mobile: "",
    password: "",
    confirm_password: "",
    dob: "",
    gender: "",
    address: "",
    city: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      setFormData({ ...formData, [name]: value.replace(/\D/g, "") });
      if (errors.mobile) setErrors({ ...errors, mobile: "" });
      return;
    }
    if (name === "email") {
      setFormData({ ...formData, [name]: value.toLowerCase() });
      if (errors.email) setErrors({ ...errors, email: "" });
      return;
    }
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim())
      newErrors.full_name = "Full name is required";

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Enter valid email";

    if (!formData.mobile) newErrors.mobile = "Mobile is required";
    else if (!/^[0-9]{10}$/.test(formData.mobile))
      newErrors.mobile = "Enter 10-digit number";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Min 6 characters";

    if (formData.password !== formData.confirm_password)
      newErrors.confirm_password = "Passwords don't match";

    if (!formData.dob) newErrors.dob = "Date of birth is required";
    else {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      )
        age--;
      if (age < 18) newErrors.dob = "Employee must be 18+ years old";
    }

    if (!formData.gender) newErrors.gender = "Please select gender";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      // ✅ Correct way - using Services function
      const response = await AddEmployee(formData);
      alert(response.message || "🎉 Employee added successfully!");
      setFormData(initialFormState);
      setErrors({});
      navigate("/dashboard/employees"); // Optionally navigate back
    } catch (error) {
      alert(error.response?.data?.message || "Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2 }, py: 2 }}>
      <form onSubmit={handleSubmit} autoComplete="off">
        {/* TOP ACTION BAR */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mb: 2,
            mt: { xs: 2, md: -3 },
          }}
        >
          <Link
            to="/employees"
            style={{
              textDecoration: "none",
              color: "#1976d2",
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} /> Back to Employees
          </Link>
        </Box>

        {/* SECTION TITLE */}
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          fontWeight="bold"
          mb={2}
          sx={{
            borderBottom: "2px solid #1976d2",
            paddingBottom: 0.5,
            display: "inline-block",
          }}
        >
          Employee Details
        </Typography>

        {/* PERSONAL INFORMATION */}
        <Box border="1px solid #ccc" borderRadius="8px" mb={2} p={2}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ mb: 1.5, color: "#555" }}
          >
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="full_name"
                label="Full Name"
                value={formData.full_name}
                onChange={handleChange}
                error={!!errors.full_name}
                helperText={errors.full_name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="email"
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="mobile"
                label="Contact Number"
                value={formData.mobile}
                onChange={handleChange}
                inputProps={{ maxLength: 10 }}
                error={!!errors.mobile}
                helperText={errors.mobile}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="dob"
                type="date"
                label="Date of Birth"
                value={formData.dob}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={!!errors.dob}
                helperText={errors.dob}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size={isMobile ? "small" : "medium"}
                error={!!errors.gender}
                required
              >
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  label="Gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
                {errors.gender && (
                  <FormHelperText>{errors.gender}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="city"
                label="City"
                value={formData.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="address"
                label="Address"
                multiline
                rows={isMobile ? 2 : 3}
                value={formData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>
          </Grid>
        </Box>

        {/* SECURITY / PASSWORD SECTION */}
        <Box border="1px solid #ccc" borderRadius="8px" mb={2} p={2}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ mb: 1.5, color: "#555" }}
          >
            Security / Login Credentials
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="password"
                type="password"
                label="Password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                name="confirm_password"
                type="password"
                label="Confirm Password"
                value={formData.confirm_password}
                onChange={handleChange}
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
                required
              />
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
                sx={{
                  height: { xs: 40, md: 56 },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Add Employee"
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Container>
  );
}

export default AddEmployeeComponent;